import { lang } from '@umu/ukit-lang';
/**
 * @desc 多选下拉框
 * @author Kody
 */
import React, { useEffect, useRef, useState } from 'react';
import { fetch } from '@umu/umu-fetch';
import { isSameObj } from '@umu/umu-util';
import { $window } from '@umu/umu-dom';
import If from '../../if';
import Loading from '../../loading/loading';

import './multiSelect.scss';

interface IProps {
    api: string; // 数据下拉接口
    placeholder: string;

    noDataTip?: string; // 无选项的文案
    method?: 'get' | 'post'; // 请求方式

    onChange: (selects: Array<{ [key: string]: any }>) => void;
    getParam(keyword: string): { [key: string]: any }; // custom param
    renderCard(item: { [key: string]: any }): JSX.Element; // select card
    renderItem(item: { [key: string]: any }): JSX.Element; // 下拉列表
    filter?(item: { [key: string]: any }): boolean; // 过滤数据
}

const defaultFilter = () => {
    return true;
};

export const MultiSelect = (props: IProps) => {
    const listRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const [isLoading, setLoading] = useState(false);
    const [searchWord, setSearchWord] = useState('');
    const [isShowList, setShowList] = useState(false); // 是否显示下拉列表
    const [selecteds, setSelecteds] = useState<Array<{ [key: string]: any }>>([]);
    const [resultList, setResultList] = useState<Array<{ [key: string]: any }>>([]);
    const { method = 'get', noDataTip = lang('无匹配项'), getParam, placeholder, filter = defaultFilter } = props;
    // 节流器
    let timer: number = 0;

    const fetchUserList = () => {
        if (!searchWord) {
            return;
        }
        setLoading(true);
        const param = getParam(searchWord);
        fetch[method](props.api, param, {}).then((res: { [key: string]: any }) => {
            const data = res.data;
            if (!res.data) {
                setResultList([]);
                setLoading(false);
                return;
            }
            const list = (Array.isArray(data) ? data : data.list) || [];
            setLoading(false);
            setResultList(list.filter(filter));
        });
    };

    const bindClickEvent = () => {
        const clickEvent = (e: MouseEvent) => {
            const containerDom = containerRef.current;
            if (!containerDom || !containerDom.contains(e.target as Node)) {
                setShowList(false);
            } else {
                setShowList(true);
            }
        };
        $window.addEventListener('click', clickEvent, false);
        return () => $window.removeEventListener('click', clickEvent, false);
    };

    useEffect(fetchUserList, [searchWord]);
    useEffect(bindClickEvent, []);

    /**
     * @desc 关键词变化，延时加载
     */
    const onWordChangeHandle = () => {
        let newWord = '';
        if (inputRef && inputRef.current) {
            newWord = inputRef.current.value;
        }
        clearTimeout(timer);
        timer = $window.setTimeout(() => {
            setSearchWord(newWord);
            clearTimeout(timer);
        }, 400);
    };

    const onSelectHandle = (item: { [key: string]: any }) => {
        let newSelecteds = [];
        const existing =
            (selecteds || []).filter((v) => {
                return isSameObj(v, item);
            }).length > 0;
        if (!existing) {
            newSelecteds = [...selecteds, item];
            setSelecteds(newSelecteds);
            props.onChange(newSelecteds);
        }
        setSearchWord('');
        setShowList(false);
        setResultList([]);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const onClose = (index: number) => {
        const newList = [...selecteds];
        newList.splice(index, 1);
        setSelecteds(newList);
        props.onChange(newList);
    };

    const renderSearchBox = () => {
        return (
            <input
                type="text"
                ref={inputRef}
                key="editor-input"
                placeholder={placeholder || ''}
                className="editor-input"
                onChange={onWordChangeHandle}
            />
        );
    };

    /**
     * @desc 已选的选项卡
     */
    const renderSelectedCards = () => {
        const cards = selecteds.map((item: { [key: string]: any }, i: number) => {
            return (
                <div className="item-card" key={item.id || i}>
                    {props.renderCard(item)}
                    <i className="icon iconfont icon-close" onClick={() => onClose(i)} />
                </div>
            );
        });
        cards.push(renderSearchBox());
        return cards;
    };

    const renderItem = (item: { [key: string]: any }, i: number) => {
        return (
            <li key={item.id || i} onClick={() => onSelectHandle(item)} className="select-item">
                {props.renderItem(item)}
            </li>
        );
    };

    /**
     * @desc 下拉列表
     */
    const renderList = () => {
        if (isLoading) {
            return null;
        }
        return (
            <ul className="multi-select-ul">
                {resultList.map(renderItem)}
                <If condition={resultList.length < 1}>
                    <li className="no-data-tip">{noDataTip}</li>
                </If>
            </ul>
        );
    };
    return (
        <div className="multi-select" ref={containerRef}>
            <div className="select-area">
                <div className="search-box">
                    <div className="select-cards clearfix">{renderSelectedCards()}</div>
                </div>
                <div className={`list-container ${isShowList ? 'show' : ''}`} ref={listRef}>
                    <div className="list-wrap">
                        <If condition={isShowList}>
                            <div className="select-list">
                                {renderList()}
                                <If condition={isLoading}>
                                    <div className="multiple-load">
                                        <Loading />
                                    </div>
                                </If>
                            </div>
                        </If>
                    </div>
                </div>
            </div>
        </div>
    );
};
